import ExcelJS from "exceljs";

const thinBorder = {
  top: { style: "thin", color: { argb: "FFCCCCCC" } },
  left: { style: "thin", color: { argb: "FFCCCCCC" } },
  bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
  right: { style: "thin", color: { argb: "FFCCCCCC" } },
};

function triggerDownload(buffer, filename) {
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function styleHeader(row) {
  row.eachCell((cell) => {
    cell.font = { bold: true, size: 11 };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF3F4F6" },
    };
    cell.border = thinBorder;
  });
}

function addSheet(wb, name, headers, rows) {
  const ws = wb.addWorksheet(name);
  ws.addRow(headers);
  styleHeader(ws.getRow(1));
  rows.forEach((r) => {
    const row = ws.addRow(r);
    row.eachCell((cell) => {
      cell.border = thinBorder;
    });
  });
  headers.forEach((_, i) => {
    ws.getColumn(i + 1).width = Math.min(
      36,
      Math.max(12, String(headers[i]).length + 4)
    );
  });
  return ws;
}

/**
 * @param {object} data — cashflow API response
 * @param {{ filename?: string }} [opts]
 */
export async function exportCashflowExcel(data, opts = {}) {
  const wb = new ExcelJS.Workbook();
  const from = data?.filters?.from_date || "";
  const to = data?.filters?.to_date || "";
  const filename = opts.filename || `bao_cao_dong_tien_${from}_${to}.xlsx`;

  addSheet(
    wb,
    "Tóm tắt",
    ["Chỉ tiêu", "Giá trị"],
    [
      ["Từ ngày", from],
      ["Đến ngày", to],
      ["Tổng thu", data?.summary?.total_in ?? 0],
      ["Tổng chi", data?.summary?.total_out ?? 0],
      ["Net", data?.summary?.net ?? 0],
    ]
  );

  addSheet(
    wb,
    "Theo tháng",
    ["Tháng", "Thu", "Chi", "Net"],
    (data?.by_month || []).map((m) => [m.label, m.total_in, m.total_out, m.net])
  );

  addSheet(
    wb,
    "Chi tiết",
    ["Ngày", "Loại", "Mã", "Mô tả", "Chi nhánh", "Thu", "Chi"],
    (data?.ledger || []).map((row) => [
      String(row.entry_date || "").slice(0, 10),
      row.entry_type_label,
      row.code,
      row.description,
      row.branch_name || "",
      row.amount_in || 0,
      row.amount_out || 0,
    ])
  );

  const buffer = await wb.xlsx.writeBuffer();
  triggerDownload(buffer, filename);
}

/**
 * @param {object} data — collections API response
 * @param {{ filename?: string }} [opts]
 */
export async function exportCollectionsExcel(data, opts = {}) {
  const wb = new ExcelJS.Workbook();
  const from = data?.filters?.from_date || "";
  const to = data?.filters?.to_date || "";
  const filename = opts.filename || `bao_cao_thu_cong_no_${from}_${to}.xlsx`;

  addSheet(
    wb,
    "Tóm tắt",
    ["Chỉ tiêu", "Giá trị"],
    [
      ["Từ ngày", from],
      ["Đến ngày", to],
      ["Tổng phiếu thu", data?.summary?.receipt_total ?? 0],
      ["Số phiếu thu", data?.summary?.receipt_count ?? 0],
      ["Tổng HĐ chưa thu", data?.summary?.unpaid_total ?? 0],
      ["Số HĐ chưa thu", data?.summary?.unpaid_count ?? 0],
    ]
  );

  addSheet(
    wb,
    "Thu theo nguồn",
    ["Nguồn", "Số phiếu", "Tổng tiền"],
    (data?.receipt_summary || []).map((r) => [
      r.payer_type_label,
      r.count,
      r.total,
    ])
  );

  addSheet(
    wb,
    "Phiếu thu",
    ["Ngày", "Mã", "Nguồn", "Người nộp", "Chi nhánh", "Số tiền", "Lý do"],
    (data?.receipts || []).map((r) => [
      String(r.receipt_date || "").slice(0, 10),
      r.receipt_code,
      r.payer_type_label,
      r.payer_name || "",
      r.branch_name || "",
      r.amount || 0,
      r.reason || "",
    ])
  );

  addSheet(
    wb,
    "HĐ chưa thu",
    ["Ngày HĐ", "Số HĐ", "Đối tác", "Chi nhánh", "Số tiền", "Trạng thái", "Số ngày treo"],
    (data?.unpaid_invoices || []).map((r) => [
      String(r.invoice_date || "").slice(0, 10),
      r.invoice_number,
      r.partner_name || "",
      r.branch_name || "",
      r.total_amount || 0,
      r.processing_status_label || r.processing_status,
      r.days_outstanding ?? 0,
    ])
  );

  const buffer = await wb.xlsx.writeBuffer();
  triggerDownload(buffer, filename);
}
